// src/components/Abstract.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Abstract.css";
// Make sure to rename your file or update this path if you have a new English PDF
import filePath from "../images/Instructions aux auteurs_CIFMIA.docx"; 

const Abstract = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    authorsAddress: "",  
    orateur: "",         
    typeResponse: "",    // Poster / Oral
    file: null,          
    message: "",         
    otherTopic: "",      
  });

  const [abstractCountdown, setAbstractCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const [loading, setLoading] = useState(false); 
  const [errors, setErrors] = useState({}); 

  // Calculate time left (Set to a logical deadline before the Nov 19 event)
  const calculateAbstractCountdown = () => {
    const eventDate = new Date("2026-09-20T23:59:59"); // Abstract deadline
    const now = new Date();
    const timeDifference = eventDate - now;

    if (timeDifference > 0) {
      return {
        days: Math.floor(timeDifference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((timeDifference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((timeDifference / (1000 * 60)) % 60),
        seconds: Math.floor((timeDifference / 1000) % 60),
      };
    }
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  };

  useEffect(() => {
    setAbstractCountdown(calculateAbstractCountdown());
    const timer = setInterval(() => {
      setAbstractCountdown(calculateAbstractCountdown());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (file) {
        if (file.size > 10 * 1024 * 1024) {
          alert("The file exceeds the 10 MB limit.");
          return;
        }
        if (file.type !== "application/pdf") {
          alert("Only PDF files are allowed!");
          return;
        }

        setFormData((prevData) => ({
          ...prevData,
          [name]: file,
        }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result.split(",")[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      firstName,
      lastName,
      email,
      phone,
      authorsAddress,
      orateur,
      typeResponse,
      file,
      message,
      otherTopic,
    } = formData;

    let formErrors = {};

    if (!firstName) formErrors.firstName = "First name is required.";
    if (!lastName) formErrors.lastName = "Last name is required.";
    if (!email) formErrors.email = "Email is required.";
    if (!phone) formErrors.phone = "Phone number is required.";
    if (!authorsAddress) formErrors.authorsAddress = "Authors' address is required.";
    if (!orateur) formErrors.orateur = "Speaker is required.";
    if (!typeResponse) formErrors.typeResponse = "Presentation type (Poster/Oral) is required.";
    if (!message) formErrors.message = "Please select a session.";
    if (message === "OTHER" && !otherTopic) {
      formErrors.otherTopic = "Please specify your session if you chose OTHER.";
    }
    if (!file) formErrors.file = "Please upload a PDF file.";

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({}); 

    let attachmentBase64 = null;
    if (file) {
      attachmentBase64 = await fileToBase64(file);
    }

    const payload = {
      firstName,
      lastName,
      email,
      phone,
      authorsAddress,
      orateur,
      typeResponse,
      attachment: attachmentBase64,
      message,
      otherTopic,
    };

    try {
      setLoading(true);

      const url = "https://us-central1-veted-africa.cloudfunctions.net/abstractSending";

      await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });

      alert("Your abstract has been submitted successfully!");
      setFormData({
        firstName: "", lastName: "", email: "", phone: "",
        authorsAddress: "", orateur: "", typeResponse: "",
        file: null, message: "", otherTopic: "",
      });
    } catch (error) {
      console.error("Error submitting abstract:", error);
      alert("Failed to submit your abstract. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="abstract-section">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">Sending...</div>
        </div>
      )}

      <div className="abstract-container">
        <div className="abstract-left">
          <h1 className="abstract-title">Abstract Submission:</h1>
          <ul className="abstract-details">
            <li>Title: Written in Times New Roman 12 pt <strong>bold</strong>.</li>
            <li><strong>Author's name:</strong> Written in Times New Roman 12 pt.</li>
            <li><strong>Email address:</strong> Written in Times New Roman 10 pt.</li>
            <li><strong>Paper format:</strong> A4</li>
            <li><strong>Orientation:</strong> Portrait - Spacing: Single throughout.</li>
            <li><strong>Body text:</strong> Times New Roman 12 pt, justified.</li>
            <li><strong>Word count:</strong> Maximum 250 words.</li>
            <li><strong>Abstract structure:</strong> Introduction, Materials & Methods, Results & Discussion, Conclusion.</li>
            <li><strong>Page limit:</strong> Maximum 1 page.</li>
          </ul>

          <a
            href={filePath}
            download="Instructions_Authors.docx"
            className="download-button"
          >
            Download Author Instructions
          </a>

          <div className="abstract-countdown-box">
            <h3 className="abstract-countdown-title">
              Time remaining to submit <br /> before 20/09/2026 :
            </h3>
            <div className="abstract-countdown-details">
              <div className="abstract-countdown-item">
                <span>{abstractCountdown.days}</span>
                <p>Days</p>
              </div>
              <div className="abstract-countdown-item">
                <span>{abstractCountdown.hours}</span>
                <p>Hours</p>
              </div>
              <div className="abstract-countdown-item">
                <span>{abstractCountdown.minutes}</span>
                <p>Minutes</p>
              </div>
              <div className="abstract-countdown-item">
                <span>{abstractCountdown.seconds}</span>
                <p>Seconds</p>
              </div>
            </div>
          </div>
        </div>

        <div className="abstract-right">
          <h2 className="form-title">Submit Your Abstract</h2>
          <form className="abstract-form" onSubmit={handleSubmit}>
            
            <div className="form-group">
              <label>First Name *</label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required />
              {errors.firstName && <span className="error">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label>Last Name *</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required />
              {errors.lastName && <span className="error">{errors.lastName}</span>}
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="example@gmail.com" required />
              {errors.email && <span className="error">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label>Phone *</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+212600000000" required />
              {errors.phone && <span className="error">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label>Authors' Address *</label>
              <input type="text" name="authorsAddress" value={formData.authorsAddress} onChange={handleChange} placeholder="Institution / Address" required />
              {errors.authorsAddress && <span className="error">{errors.authorsAddress}</span>}
            </div>

            <div className="form-group">
              <label>Lead Speaker *</label>
              <input type="text" name="orateur" value={formData.orateur} onChange={handleChange} placeholder="Name of the main speaker" required />
              {errors.orateur && <span className="error">{errors.orateur}</span>}
            </div>

            <div className="form-group">
              <label>Presentation Type *</label>
              <select name="typeResponse" value={formData.typeResponse} onChange={handleChange} required>
                <option value="">-- Select --</option>
                <option value="Poster">Poster</option>
                <option value="Oral">Oral</option>
              </select>
              {errors.typeResponse && <span className="error">{errors.typeResponse}</span>}
            </div>

            <div className="form-group">
              <label>Upload Abstract (PDF only) *</label>
              <input type="file" name="file" accept=".pdf" onChange={handleChange} required />
              {errors.file && <span className="error">{errors.file}</span>}
            </div>

            <div className="form-group">
              <label>Session *</label>
              <div className="radio-group">
                <label>
                  <input type="radio" name="message" value="Artificial Intelligence in Veterinary Sciences" checked={formData.message === "Artificial Intelligence in Veterinary Sciences"} onChange={handleChange} required />
                  Artificial Intelligence in Veterinary Sciences
                </label>
                <label>
                  <input type="radio" name="message" value="Veterinary Curriculum Update" checked={formData.message === "Veterinary Curriculum Update"} onChange={handleChange} />
                  Veterinary Curriculum Update
                </label>
                <label>
                  <input type="radio" name="message" value="Advancing One Health Strategies and Transboundary Diseases" checked={formData.message === "Advancing One Health Strategies and Transboundary Diseases"} onChange={handleChange} />
                  Advancing One Health Strategies and Transboundary Diseases
                </label>
                <label>
                  <input type="radio" name="message" value="Combating Antimicrobial Resistance" checked={formData.message === "Combating Antimicrobial Resistance"} onChange={handleChange} />
                  Combating Antimicrobial Resistance
                </label>
                <label>
                  <input type="radio" name="message" value="OTHER" checked={formData.message === "OTHER"} onChange={handleChange} />
                  OTHER
                </label>
              </div>
              {formData.message === "OTHER" && (
                <textarea name="otherTopic" value={formData.otherTopic} onChange={handleChange} placeholder="Enter your custom session topic..." rows={3} style={{ marginTop: "0.5rem", width: "100%" }} />
              )}
              {errors.message && <span className="error">{errors.message}</span>}
              {errors.otherTopic && <span className="error">{errors.otherTopic}</span>}
            </div>

            <button type="submit" className="submit-button" disabled={loading}>
              Submit
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Abstract;